// src/pages/drone/buah-alami/components/MapViewTooltip.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
    Tooltip,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Alert,
    CircularProgress,
    Snackbar
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MapIcon from '@mui/icons-material/Map';
import { ref, get } from 'firebase/database';

const MapViewTooltip = ({ 
    database, 
    id_lokasi, 
    lokasiNama, 
    firebaseKey,
    children // Prop untuk children
}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const [openViewer, setOpenViewer] = useState(false);
    const [existingMapLink, setExistingMapLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    // Ref untuk dialog
    const dialogRef = useRef(null);

    // Cek apakah sudah ada peta
    useEffect(() => {
        const checkExistingMap = async () => {
            if (!firebaseKey || !id_lokasi) return;
            
            try {
                setLoading(true);
                const mapRef = ref(database, `db_drone/tb_buah_alami/${firebaseKey}`);
                const snapshot = await get(mapRef);
                
                if (snapshot.exists()) {
                    const data = snapshot.val();
                    setExistingMapLink(data.map_pdf_link || '');
                }
            } catch (error) {
                console.error('Error checking existing map:', error);
            } finally {
                setLoading(false);
            }
        };
        
        checkExistingMap();
    }, [database, firebaseKey, id_lokasi]);

    const handleTooltipClick = (event) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleViewMap = () => {
        if (existingMapLink) {
            setOpenViewer(true);
        } else {
            setSnackbar({
                open: true,
                message: 'Tidak ada peta PDF yang tersimpan untuk lokasi ini',
                severity: 'warning'
            });
        }
        handleMenuClose();
    };

    const handleViewerClose = () => {
        setOpenViewer(false);
    };

    const hasMap = !!existingMapLink;

    return (
        <>
            {/* Tooltip dengan children - SAMA PERSIS seperti MapActionTooltip */}
            <Tooltip 
                title="Lihat Peta PDF" 
                arrow
                placement="top"
            >
                <Box
                    onClick={handleTooltipClick}
                    sx={{ 
                        cursor: 'pointer',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center'
                    }}
                >
                    {children}
                </Box>
            </Tooltip>

            {/* Menu Aksi - HANYA VIEW saja */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <MenuItem 
                    onClick={handleViewMap}
                    disabled={!hasMap || loading}
                >
                    <ListItemIcon>
                        {loading ? (
                            <CircularProgress size={16} />
                        ) : (
                            <VisibilityIcon fontSize="small" />
                        )}
                    </ListItemIcon>
                    <ListItemText>
                        {loading ? 'Memeriksa...' : hasMap ? 'Lihat Peta PDF' : 'Tidak ada peta'}
                    </ListItemText>
                </MenuItem>
            </Menu>

            {/* Dialog View PDF - SAMA seperti MapActionTooltip */}
            <Dialog
                open={openViewer}
                onClose={handleViewerClose}
                maxWidth="xl"
                fullWidth
                fullScreen={true}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MapIcon />
                        Peta PDF - {lokasiNama}
                    </Box>
                    <IconButton
                        aria-label="close"
                        onClick={handleViewerClose}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <VisibilityIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0, height: 'calc(100vh - 64px)' }}>
                    {existingMapLink && (
                        <>
                            <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <PictureAsPdfIcon />
                                        <span>Peta PDF untuk lokasi: <strong>{lokasiNama}</strong></span>
                                    </Box>
                                </Alert>
                                <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                                    URL: {existingMapLink}
                                </Box>
                            </Box>
                            <iframe
                                src={existingMapLink}
                                title={`Peta PDF - ${lokasiNama}`}
                                width="100%"
                                height="100%"
                                style={{ border: 'none' }}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleViewerClose} 
                        variant="outlined"
                    >
                        Tutup
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar - SAMA seperti MapActionTooltip */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default MapViewTooltip;